import {
  Form,
  Button,
  Container,
  FormField,
  Grid,
  Header,
  SpaceBetween,
  SelectProps,
  Alert,
} from '@amzn/awsui-components-core';
import { DateTime } from 'luxon';
import React, { useEffect, useState, useMemo, useContext } from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { IFeedbackServiceClient, FeedbackServiceFactory } from '../../api/FeedbackService';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { displayAuthorInfo, searchEmployeeProfile } from '../../api/UtilsApi';
import { isUUID } from 'class-validator';
import LoadingIndicator from '../common/LoadingIndicator';
import NotFound from '../common/NotFound';
import UnAuthorized from '../common/UnAuthorized';
import ReactRouterProps from '../utils/ReactRouterProps';
import { getContentPath } from '../../common/ContentPaths';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import BrowserHistory from '../utils/BrowserHistory';
import HttpStatusCode from '../../api/HttpStatusCode';
import Publication from '../../model/Publication';
import { ContentType, UserType } from '@amzn/awscat-pattern-library-service-model';
import { contentSubTypeDisplayNames } from '../utils/DisplayNames';
import { FeedbackRating } from '../feedback-service';
import { AppContext } from '../../contexts/AppContext';
import { convertEntities } from '@amzn/awscat-editor';
import { FeedbackStrategy } from './strategies/FeedbackStrategy';
import { KudosFeedbackStrategy } from './strategies/KudosFeedbackStrategy';
import { DocumentFeedbackStrategy } from './strategies/DocumentFeedbackStrategy';

const mplService: IMplServiceClient = MplServiceClientFactory.create();

const PublicationFeedbackPage = (props: ReactRouterProps) => {
  const feedbackServiceClient: IFeedbackServiceClient = useMemo(
    () => FeedbackServiceFactory.create(),
    [],
  );
  const contentId = decodeURIComponent(props.match.params.contentId);
  // @ts-expect-error TS2345 - strict migration
  const ratingType = decodeURIComponent(props.match.params.ratingType);
  const [isLoading, setIsLoading] = useState(false);
  // @ts-expect-error TS2345 - strict migration
  const [publication, setPublication] = useState<Publication>(null);
  const [statusCode, setStatusCode] = useState(null);
  const flashbarMessageBroker = FlashbarMessageBroker.create();

  const [selectedIssue, setSelectedIssue] = useState(null);
  const [improvementFeedbackText, setImprovementFeedbackText] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [actionFeedbackText, setActionFeedbackText] = useState<string>('');
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  const [ownerProfileNotFound, setOwnerProfileNotFound] = useState<boolean>(false);
  const [isDescriptionValid, setIsDescriptionValid] = useState(false);
  const appContext = useContext(AppContext);
  // @ts-expect-error TS18048 - strict migration
  const { userId } = appContext.authnInfo.userProfile;
  const entities = appContext?.entities || {};

  // @ts-expect-error TS2322 - strict migration
  const strategy: FeedbackStrategy = useMemo(() => {
    if (!publication) return null;
    const isPost = publication.content.contentMetadata?.contentType === ContentType.Post;
    const isKudos = ratingType === FeedbackRating.Like;

    if (isPost) {
      // We no longer provide links to allow users to add feedback for posts.
      // There is no way for the user to reach this page in the first place.
      //
      // Crashing here is intentional so the error will be reported to RUM,
      // and the error page will be displayed by the ErrorBoundary.
      throw new Error('Providing feedback on posts is no longer supported');
    }
    if (isKudos) return new KudosFeedbackStrategy();
    return new DocumentFeedbackStrategy();
  }, [publication, ratingType]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true);
        const publishedContent = await mplService.getPublication(contentId);
        const { userId: ownerId, type } = publishedContent.contentOwner;

        if (type === UserType.AWS) {
          const ownerProfile = await searchEmployeeProfile(ownerId);
          setOwnerProfileNotFound(!ownerProfile);
        }

        setPublication(publishedContent);
      } catch (e) {
        // @ts-expect-error TS2339 - strict migration
        setStatusCode(e?.response.status);
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, [contentId]);

  if (!isUUID(contentId)) {
    return <NotFound />;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (statusCode === HttpStatusCode.Forbidden) {
    return <UnAuthorized />;
  } else if (statusCode === HttpStatusCode.NotFound) {
    return <NotFound />;
  }

  if (!publication || !strategy) {
    return null;
  }

  const isFormValid = isDescriptionValid;

  const validateForm = () => {
    return strategy.validate({
      selectedIssue,
      improvementFeedbackText,
      comment,
      actionFeedbackText,
      isFormValid,
    });
  };

  const handleSubmitFeedback = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSubmiting(true);
    try {
      await strategy.submit(
        { selectedIssue, improvementFeedbackText, comment, actionFeedbackText, isFormValid },
        { contentId, userId, publication, mplService, feedbackServiceClient },
      );
      backToContentPage();
      flashbarMessageBroker.pushSuccessMessage(
        'We have successfully received your feedback and have reported to the author.',
        0,
        'Thank you for your feedback',
      );
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      flashbarMessageBroker.pushErrorMessage('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmiting(false);
    }
  };

  const backToContentPage = () =>
    BrowserHistory.push(getContentPath({ contentId, mode: 'publication' }));

  const { content, contentOwner, publicationDate } = publication;

  const isPost = publication.content.contentMetadata?.contentType === ContentType.Post;

  const subjectContainer = (
    <Container header={<Header variant="h3">Subject</Header>}>
      <SpaceBetween size="m">
        <FormField
          label={strategy.getSubjectLabel()}
          description={convertEntities(content?.contentMetadata?.contentName, entities)}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        />
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          {isPost ? (
            <FormField
              label="Type"
              description={contentSubTypeDisplayNames.get(
                content.contentTypeMetadata.contentSubType,
              )}
              i18nStrings={{ errorIconAriaLabel: 'Error' }}
            />
          ) : (
            <FormField
              label="Author"
              description={displayAuthorInfo(contentOwner.name, contentOwner.organizationName)}
              i18nStrings={{ errorIconAriaLabel: 'Error' }}
            />
          )}
          <FormField
            label={strategy.getDateLabel()}
            description={publicationDate.toLocaleString(DateTime.DATE_MED)}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          />
        </Grid>
      </SpaceBetween>
    </Container>
  );

  const feedbackContainer = (
    <Container header={<Header variant="h3">Feedback</Header>}>
      {strategy.renderFormFields(
        { selectedIssue, improvementFeedbackText, comment, actionFeedbackText, isFormValid },
        {
          // @ts-expect-error TS2322 - strict migration
          setSelectedIssue,
          setImprovementFeedbackText,
          setComment,
          setActionFeedbackText,
          onDescriptionValidationChange: setIsDescriptionValid,
        },
      )}
    </Container>
  );

  const ownerNotFoundAlert = (
    <Alert
      onDismiss={() => setOwnerProfileNotFound(false)}
      visible={ownerProfileNotFound}
      dismissAriaLabel="Close"
      type="error"
      header="Unable to find author internal profile"
    >
      The content author may no longer be with Amazon. This may lead to SIM ticket in unassigned
      state or not being created.
    </Alert>
  );

  const formActions = (
    <SpaceBetween direction="horizontal" size="xs">
      <Button variant="link" onClick={backToContentPage}>
        Cancel
      </Button>
      <Button
        variant="primary"
        formAction="submit"
        loading={isSubmiting}
        disabled={!isFormValid}
        onClick={handleSubmitFeedback}
      >
        Submit feedback
      </Button>
    </SpaceBetween>
  );

  return (
    <Form
      className="publication-feedback-form"
      actions={formActions}
      header={<Header variant="h2">{strategy.getHeaderText()}</Header>}
    >
      <SpaceBetween size="l">
        {subjectContainer}
        {ownerNotFoundAlert}
        {feedbackContainer}
      </SpaceBetween>
    </Form>
  );
};

export default PublicationFeedbackPage;
